import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { ChevronLeftIcon } from '@heroicons/react/outline';
import { Affix, Layout, Tooltip } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import MenuIcon from 'assets/Icons/MenuIcon';
import LeftRotateIcon from 'assets/Icons/rotateIcon/LeftRotateIcon';
import RightRotateIcon from 'assets/Icons/rotateIcon/RightRotateIcon';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { UAParser } from 'ua-parser-js';

import { aiModalOpenAtom } from '@/atoms/pdfStates/aiModalOpenAtom';
import { drawerPdfAtom } from '@/atoms/pdfStates/drawerAtom';
import { iframeModalAtom } from '@/atoms/pdfStates/iframeModalAtom';
import { mobileToolboxVisibleAtom } from '@/atoms/pdfStates/mobileToolboxVisible';

import { ShouldRender } from '../shared/basic/ShouldRender';
import { isBannerVisible } from '../../atoms/isBannerVisible';
import DoublePageIcon from '../../../assets/Icons/DoublePageIcon';
import SinglePageIcon from '../../../assets/Icons/SinglePageIcon';

const { Header } = Layout;

interface Props {
  isFullScreen: boolean;
  toggleFullScreen: () => void;
  zoomValue: number;
  zoomOut: () => void;
  zoomIn: () => void;
  sideBySide: boolean;
  title: string;
  rotateLeft: (pageNumber: number) => void;
  pageNumber: number;
  rotateRight: (pageNumber: number) => void;
  toggleSideBySide: () => void;
  openDrawer: boolean;
  isModal?: boolean;
  handleCancel?: () => void;
  headerClass?: string;
  isIFrame?: boolean;
  fullScreenIcon?: React.ReactNode;
}

const PdfReaderHeader: React.FC<Props> = ({
  isFullScreen,
  toggleFullScreen,
  zoomValue,
  zoomOut,
  zoomIn,
  sideBySide,
  title,
  rotateLeft,
  pageNumber,
  rotateRight,
  toggleSideBySide,
  openDrawer,
  headerClass,
  isModal = false,
  isIFrame = false,
  fullScreenIcon,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [, setMobileToolboxVisible] = useRecoilState(mobileToolboxVisibleAtom);
  const [, setDrawerState] = useRecoilState(drawerPdfAtom);
  const router = useRouter();
  const screens = useBreakpoint();
  const isBannerVisibleValue = useRecoilValue(isBannerVisible);
  const [, setOpenDrawer] = useRecoilState<boolean>(aiModalOpenAtom);
  const [, setIsModalVisible] = useRecoilState(iframeModalAtom);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 56);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const isIphone = (): boolean => {
    const parser = new UAParser();
    const device = parser.getDevice();
    return device.model === 'iPhone';
  };

  return (
    <Affix offsetTop={0}>
      <Header
        className={clsx(
          headerClass ??
            `${isFullScreen && 'fixed z-20'} bg-bgColor bg-opacity-75 px-5 lg:px-10 backdrop-blur-md flex border-bgColor border-t w-full transition-all`,
          !isScrolled
            ? 'mt-0'
            : isBannerVisibleValue && !screens.md
              ? ''
              : 'mt-0',
        )}
      >
        <div className='flex justify-between items-center h-full w-full text-fadedWhite'>
          <div className='flex items-center gap-x-1 md:gap-x-4'>
            <ShouldRender check={!isIFrame}>
              <div
                className='group flex items-center cursor-pointer md:gap-x-3'
                onClick={() => {
                  isFullScreen && toggleFullScreen();
                  setOpenDrawer(false);
                  setDrawerState({
                    contentDrawer: false,
                    notesDrawer: false,
                  });
                  if (isIFrame || isModal) setIsModalVisible(false);
                  else router.push('/books');
                }}
              >
                <div className='flex items-center justify-center md:bg-cartBgColor group-hover:bg-gray-200 group-hover:bg-opacity-15 p-1 w-7 h-7 md:w-6 md:h-6 rounded-full'>
                  <ChevronLeftIcon className='w-5 md:w-4' />
                </div>
                <ShouldRender check={screens.md}>
                  <span className='truncate w-[20vw]'>{title}</span>
                  <span className='border h-1/3 border-backGroundReaderTabBg' />
                </ShouldRender>
              </div>
            </ShouldRender>
            <ShouldRender check={isIFrame}>
              <span className='truncate font-bold text-white w-[30vw] md:w-[20vw]'>
                {title}
              </span>
            </ShouldRender>
          </div>

          <div
            className={clsx(
              isIphone() ? 'right-[5vw]' : 'left-1/2 -translate-x-1/2',
              'absolute  top-1/2 transform   mt-1 md:mt-0 -translate-y-1/2',
            )}
          >
            <div className='flex items-center gap-x-4'>
              <Tooltip title={`Zoom Out ${zoomValue}%`} placement='bottom'>
                <button onClick={zoomOut} className='text-white'>
                  <ZoomOutOutlined style={{ fontSize: 20 }} />
                </button>
              </Tooltip>

              <Tooltip title={`Zoom In ${zoomValue}%`} placement='bottom'>
                <button onClick={zoomIn} className='text-white'>
                  <ZoomInOutlined style={{ fontSize: 20 }} />
                </button>
              </Tooltip>

              <ShouldRender check={screens.md}>
                <span className='border h-4 border-backGroundReaderTabBg' />
              </ShouldRender>

              <ShouldRender check={screens.md && !openDrawer}>
                <motion.button
                  onClick={toggleSideBySide}
                  aria-pressed={sideBySide}
                  className='relative flex items-center w-16 h-8 rounded-full cursor-pointer px-1 bg-[#1C1C1C]'
                  layout
                >
                  <motion.div
                    className='absolute left-[12px] z-20'
                    animate={{ color: sideBySide ? '#FFFFFF99' : '#1C1C1C' }}
                  >
                    <SinglePageIcon />
                  </motion.div>
                  <motion.div
                    className='absolute right-1.5 z-20'
                    animate={{ color: sideBySide ? '#1C1C1C' : '#FFFFFF99' }}
                  >
                    <DoublePageIcon />
                  </motion.div>
                  <motion.div
                    className='w-7 h-7 rounded-full z-10'
                    layout
                    animate={{
                      x: sideBySide ? 32 : 0,
                      backgroundColor: sideBySide ? '#FFFFFF' : '#F0F0F0',
                      boxShadow: sideBySide
                        ? '0px 0px 12px rgba(0, 102, 255, 0.5)'
                        : '0px 0px 6px rgba(0, 0, 0, 0.1)',
                    }}
                    transition={{ duration: 0.4 }}
                  />
                </motion.button>
              </ShouldRender>

              <ShouldRender check={!sideBySide && screens.md}>
                <span className='border h-4 border-backGroundReaderTabBg mx-4' />
                <div className='flex gap-x-2'>
                  <Tooltip title='Rotate Anti-Clockwise' placement='bottom'>
                    <button
                      onClick={() => rotateLeft(pageNumber)}
                      className='flex justify-center items-center p-2 w-10 h-10 rounded-full hover:bg-gray-200 hover:bg-opacity-15'
                    >
                      <LeftRotateIcon />
                    </button>
                  </Tooltip>
                  <Tooltip title='Rotate Clockwise' placement='bottom'>
                    <button
                      onClick={() => rotateRight(pageNumber)}
                      className='flex justify-center items-center p-2 w-10 h-10 rounded-full hover:bg-gray-200 hover:bg-opacity-15'
                    >
                      <RightRotateIcon />
                    </button>
                  </Tooltip>
                </div>
              </ShouldRender>
            </div>
          </div>

          <ShouldRender check={!screens.lg && !isIphone()}>
            {isIFrame ? (
              <button onClick={toggleFullScreen}>{fullScreenIcon}</button>
            ) : (
              <div
                onClick={() => setMobileToolboxVisible(true)}
                className='flex gap-x-1 items-center cursor-pointer'
              >
                <MenuIcon />
                <p className='font-semibold text-sm'>More</p>
              </div>
            )}
          </ShouldRender>
        </div>
      </Header>
    </Affix>
  );
};

export default PdfReaderHeader;
